import { appConfig } from "@ir/config";
import type { SessionResponse } from "@ir/types";
import { adminApi, request } from "./http";

/**
 * Session handling for the admin surface.
 *
 * Sign-in is a full-page redirect through dave-service, not a fetch: the OAuth
 * authorization-code exchange happens server-side so the Google client secret
 * and the resulting session token never exist in the browser. All the SPA can
 * do is ask "who am I?" and "sign me out".
 */

export function buildLoginUrl(options: {
  returnTo?: string;
  invite?: string | null;
} = {}): string {
  const params = new URLSearchParams();
  params.set("returnTo", options.returnTo ?? window.location.pathname + window.location.search);
  if (options.invite) params.set("invite", options.invite);
  return `${appConfig.adminApiUrl}/auth/login?${params.toString()}`;
}

export function startGoogleLogin(options: { returnTo?: string; invite?: string | null } = {}): void {
  window.location.assign(buildLoginUrl(options));
}

/**
 * Re-reads authorization from Cosmos on every call, which is what makes a
 * disabled user lose access immediately rather than at cookie expiry.
 */
export async function getSession(signal?: AbortSignal): Promise<SessionResponse> {
  try {
    const response = await request(
      () => adminApi.get<SessionResponse>("/auth/session", { signal }),
      "Failed to check your sign-in status.",
    );
    return response.data;
  } catch (error) {
    // A 401 here is the normal "not signed in" answer, not a failure.
    if (
      typeof error === "object" &&
      error !== null &&
      "status" in error &&
      (error as { status: number }).status === 401
    ) {
      return { authenticated: false, user: null };
    }
    throw error;
  }
}

export async function signOut(): Promise<void> {
  await request(
    () => adminApi.post("/auth/logout", {}),
    "Failed to sign you out.",
  );
}
